import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import accountsApi from "../../services/accountsCommonService";
import AccountsConstants from "../../common/AccountsConstants";
import { formatDate, formatINR, todayInput } from "./accountUser";
import { EntryPreview, settlementLink } from "./VoucherEntry";
import { ApiAlert, CreditPanel, FormDialog, ItemsTable, Notice, Pager, PRODUCTS, ReasonDialog, StatusChip } from "./AccountWidgets";
import PageShell from "./PageShell";
import "./accounts.css";

const qtyOk = (text) => /^\d+(\.\d{1,3})?$/.test(String(text).trim()) && Number(text) > 0;
const rateOk = (text) => /^\d+(\.\d{1,2})?$/.test(String(text).trim()) && Number(text) > 0;

function itemLabel(items) {
  return (items || []).map((item) => `${item.quantity} ${item.unit || "BAG"} ${item.description}`).join(", ");
}

export function SalesOrdersPage() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filters, setFilters] = useState({ status: "", customerId: "", search: "" });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [notice, setNotice] = useState(null);
  const [reason, setReason] = useState(null);

  const load = async (next = filters, nextPage = page, nextLimit = limit) => {
    setLoading(true);
    setError(null);
    try {
      const result = await accountsApi.orders({ ...next, search: next.search.trim(), page: nextPage, limit: nextLimit });
      setRows(result?.data || []);
      setTotal(result?.pagination?.total || 0);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(filters, 1, limit);
    accountsApi.customers({ isActive: "true" }).then((result) => setCustomers(result?.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFilter = (key) => (event) => {
    const next = { ...filters, [key]: event.target.value };
    setFilters(next);
    setPage(1);
    load(next, 1, limit);
  };

  return (
    <PageShell title="Sales Orders" subtitle="Credit check runs when the order is saved" actions={<button type="button" className="btn btn-primary" onClick={() => setDialog(true)}>New order</button>}>
      <ApiAlert error={error} />
      {notice && <Notice tone={notice.tone}>{notice.text}</Notice>}
      <div className="accounts-panel accounts-filter-grid">
        <label>Status
          <select className="form-select" value={filters.status} onChange={setFilter("status")}>
            <option value="">All</option>
            <option value="CREDIT_HOLD">Credit hold</option>
            <option value="APPROVED">Approved</option>
            <option value="LOADING">Loading</option>
            <option value="INVOICED">Invoiced</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </label>
        <label>Customer
          <select className="form-select" value={filters.customerId} onChange={setFilter("customerId")}>
            <option value="">All customers</option>
            {customers.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </label>
        <label>Order number<input className="form-control" value={filters.search} placeholder="Order number" onChange={setFilter("search")} /></label>
      </div>
      <div className={`accounts-table-wrap ${loading ? "accounts-dim" : ""}`}>
        <table className="accounts-table">
          <thead><tr><th>Date</th><th>Order No</th><th>Customer</th><th>Items</th><th className="num">Amount</th><th>Status</th><th>Next step</th></tr></thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{formatDate(row.orderDate)}</td>
                <td>{row.orderNo}</td>
                <td>{row.customerName}</td>
                <td>{itemLabel(row.items)}</td>
                <td className="num">{formatINR(row.totalAmount)}</td>
                <td>
                  <StatusChip status={row.status} />
                  <div className="accounts-steps">{["Order", "Approved", "Loading", "Invoiced"].map((step, index) => {
                    const current = { CREDIT_HOLD: 0, APPROVED: 1, LOADING: 2, INVOICED: 3, CANCELLED: -1 }[row.status];
                    return <span key={step} className={index === current ? "on" : index < current ? "done" : ""}>{step}</span>;
                  })}</div>
                  {row.status === "CREDIT_HOLD" && row.creditCheck && <div className="accounts-hint">Over limit by {formatINR(row.creditCheck.exceededBy)}</div>}
                </td>
                <td className="accounts-actions">
                  {row.status === "CREDIT_HOLD" && <button type="button" className="btn btn-sm btn-primary" onClick={() => setReason({ kind: "approve", row })}>Approve</button>}
                  {row.status === "APPROVED" && <button type="button" className="btn btn-sm btn-primary" onClick={() => setReason({ kind: "advice", row })}>Loading advice</button>}
                  {(row.status === "CREDIT_HOLD" || row.status === "APPROVED") && <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setReason({ kind: "cancel", row })}>Cancel</button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7}>No sales orders match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={page} limit={limit} total={total} sizes={[10, 20, 50]} onPage={(next) => { setPage(next); load(filters, next, limit); }} onLimit={(next) => { setLimit(next); setPage(1); load(filters, 1, next); }} />
      {dialog && <OrderDialog customers={customers.filter((row) => row.isActive !== false)} onClose={() => setDialog(false)} onSaved={(result) => { setDialog(false); setNotice({ tone: result.data.status === "APPROVED" ? "ok" : "warn", text: result.message }); load(); }} />}
      {reason?.kind === "approve" && (
        <ReasonDialog title={`Approve ${reason.row.orderNo} over the credit limit`} label="Approval note" confirm="Approve" dismiss="Keep order" onClose={() => setReason(null)}
          onConfirm={async (note) => { await accountsApi.approveOrder(reason.row.id, note); setReason(null); load(); }} />
      )}
      {reason?.kind === "cancel" && (
        <ReasonDialog title={`Cancel ${reason.row.orderNo}`} label="Reason" confirm="Cancel order" dismiss="Keep order" onClose={() => setReason(null)}
          onConfirm={async (text) => { await accountsApi.cancelOrder(reason.row.id, text); setReason(null); load(); }} />
      )}
      {reason?.kind === "advice" && (
        <AdviceDialog order={reason.row} onClose={() => setReason(null)} onSaved={(result) => { setReason(null); setNotice({ tone: "ok", text: `Loading advice ${result.data.adviceNo} issued. Raise the invoice from the Loading Advice page once the lorry is loaded.` }); load(); }} />
      )}
    </PageShell>
  );
}

function OrderDialog({ customers, onClose, onSaved }) {
  const [customerId, setCustomerId] = useState("");
  const [orderDate, setOrderDate] = useState(todayInput());
  const [remarks, setRemarks] = useState("");
  const [items, setItems] = useState([{ description: "", quantity: "", rate: "" }]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const total = useMemo(() => items.reduce((sum, item) => sum + (qtyOk(item.quantity) && rateOk(item.rate) ? Number(item.quantity) * Number(item.rate) : 0), 0), [items]);
  const valid = customerId && orderDate && orderDate <= todayInput() && items.some((item) => item.description.trim() && qtyOk(item.quantity) && rateOk(item.rate));

  const save = async (event) => {
    event.preventDefault();
    if (!valid) return;
    setSaving(true);
    setError(null);
    try {
      const result = await accountsApi.saveOrder({
        customerId,
        orderDate,
        remarks: remarks.trim(),
        items: items.filter((item) => item.description.trim()).map((item) => ({
          description: item.description.trim(),
          quantity: String(item.quantity).trim(),
          rate: String(item.rate).trim(),
        })),
      });
      onSaved(result);
    } catch (err) {
      setError(err);
      setSaving(false);
    }
  };

  return (
    <FormDialog
      title="New sales order"
      onClose={onClose}
      onSubmit={save}
      wide
      footer={(
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Close</button>
          <button type="submit" className="btn btn-primary" disabled={!valid || saving}>{saving ? "Saving…" : "Save order"}</button>
        </>
      )}
    >
      <ApiAlert error={error} />
      <div className="accounts-entry">
        <div className="accounts-form-grid">
          <label className="tally-field">Customer *
            <select className="form-select" value={customerId} onChange={(event) => setCustomerId(event.target.value)}>
              <option value="">Select</option>
              {customers.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
            </select>
          </label>
          <label className="tally-field">Order date *
            <input className="form-control" type="date" max={todayInput()} value={orderDate} onChange={(event) => setOrderDate(event.target.value)} />
          </label>
          {items.map((item, index) => (
            <div className="accounts-line span-2" key={index}>
              <label className="tally-field">Product *
                <input className="form-control" list="account-products" value={item.description} placeholder="Product name" onChange={(event) => setItems(items.map((row, i) => i === index ? { ...row, description: event.target.value } : row))} />
              </label>
              <label className="tally-field">Bags *
                <input className="form-control" value={item.quantity} placeholder="Bags" onChange={(event) => setItems(items.map((row, i) => i === index ? { ...row, quantity: event.target.value } : row))} />
              </label>
              <label className="tally-field">Rate *
                <input className="form-control" value={item.rate} placeholder="Rate" onChange={(event) => setItems(items.map((row, i) => i === index ? { ...row, rate: event.target.value } : row))} />
              </label>
              <div className="tally-field">Amount
                <strong>{qtyOk(item.quantity) && rateOk(item.rate) ? formatINR(Number(item.quantity) * Number(item.rate)) : "—"}</strong>
                {items.length > 1 && <button type="button" className="btn btn-link" onClick={() => setItems(items.filter((_, i) => i !== index))}>Remove</button>}
              </div>
            </div>
          ))}
          <datalist id="account-products">{PRODUCTS.map((name) => <option key={name} value={name} />)}</datalist>
          <div className="span-2 accounts-form-actions start">
            <button type="button" className="btn btn-outline-secondary" onClick={() => setItems([...items, { description: "", quantity: "", rate: "" }])}>Add item</button>
            <span>Order total <strong>{formatINR(total)}</strong></span>
          </div>
          <label className="tally-field span-2">Remarks
            <textarea className="form-control" rows={2} value={remarks} placeholder="Optional note" onChange={(event) => setRemarks(event.target.value)} />
          </label>
        </div>
        <div className="accounts-panel">
          <h3>Credit check</h3>
          <CreditPanel customerId={customerId} orderAmount={total} />
        </div>
      </div>
    </FormDialog>
  );
}

function AdviceDialog({ order, onClose, onSaved }) {
  const [form, setForm] = useState({ adviceDate: todayInput(), vehicleNo: "", driverName: "", driverPhone: "" });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const result = await accountsApi.saveAdvice({ orderId: order.id, ...form, vehicleNo: form.vehicleNo.trim() });
      onSaved(result);
    } catch (err) {
      setError(err);
      setSaving(false);
    }
  };
  return (
    <FormDialog
      title={`Loading advice for ${order.orderNo}`}
      onClose={onClose}
      onSubmit={save}
      footer={(
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Close</button>
          <button type="submit" className="btn btn-primary" disabled={saving || !form.vehicleNo.trim() || !form.adviceDate}>{saving ? "Saving…" : "Issue advice"}</button>
        </>
      )}
    >
      <p className="accounts-dialog-note">{order.customerName}</p>
      <p className="accounts-hint">{itemLabel(order.items)}</p>
      <ApiAlert error={error} />
      <div className="accounts-form-grid">
        <label className="tally-field">Date *
          <input className="form-control" type="date" max={todayInput()} value={form.adviceDate} onChange={(event) => setForm({ ...form, adviceDate: event.target.value })} />
        </label>
        <label className="tally-field">Vehicle number *
          <input className="form-control" value={form.vehicleNo} placeholder="TN01AB1234" onChange={(event) => setForm({ ...form, vehicleNo: event.target.value })} />
        </label>
        <label className="tally-field">Driver name
          <input className="form-control" value={form.driverName} placeholder="Driver name" onChange={(event) => setForm({ ...form, driverName: event.target.value })} />
        </label>
        <label className="tally-field">Driver phone
          <input className="form-control" value={form.driverPhone} placeholder="Driver phone" onChange={(event) => setForm({ ...form, driverPhone: event.target.value })} />
        </label>
      </div>
    </FormDialog>
  );
}

export function LoadingAdvicesPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ status: "", search: "" });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [invoiceFor, setInvoiceFor] = useState(null);
  const [cancelFor, setCancelFor] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = async (next = filters, nextPage = page, nextLimit = limit) => {
    setLoading(true);
    setError(null);
    try {
      const result = await accountsApi.advices({ ...next, search: next.search.trim(), page: nextPage, limit: nextLimit });
      setRows(result?.data || []);
      setTotal(result?.pagination?.total || 0);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(filters, 1, limit); }, []);
  const setFilter = (key) => (event) => {
    const next = { ...filters, [key]: event.target.value };
    setFilters(next);
    setPage(1);
    load(next, 1, limit);
  };

  return (
    <PageShell title="Loading Advice">
      <ApiAlert error={error} />
      {notice && <Notice tone="ok">{notice}</Notice>}
      <div className="accounts-panel accounts-filter-grid">
        <label>Status
          <select className="form-select" value={filters.status} onChange={setFilter("status")}>
            <option value="">All</option>
            <option value="OPEN">Open</option>
            <option value="INVOICED">Invoiced</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </label>
        <label>Advice, order or vehicle<input className="form-control" value={filters.search} placeholder="Advice, order or vehicle" onChange={setFilter("search")} /></label>
      </div>
      <div className={`accounts-table-wrap ${loading ? "accounts-dim" : ""}`}>
        <table className="accounts-table">
          <thead><tr><th>Date</th><th>Advice No</th><th>Order</th><th>Customer</th><th>Load</th><th>Vehicle</th><th className="num">Amount</th><th>Status</th><th>Next step</th></tr></thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{formatDate(row.adviceDate)}</td>
                <td>{row.adviceNo}</td>
                <td>{row.orderNo}</td>
                <td>{row.customerName}</td>
                <td>{itemLabel(row.items)}</td>
                <td>{row.vehicleNo}</td>
                <td className="num">{formatINR(row.totalAmount)}</td>
                <td><StatusChip status={row.status} /></td>
                <td className="accounts-actions">
                  {row.status === "OPEN" && <button type="button" className="btn btn-sm btn-primary" onClick={() => setInvoiceFor(row)}>Raise invoice</button>}
                  {row.status === "OPEN" && <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setCancelFor(row)}>Cancel</button>}
                  {row.status === "INVOICED" && row.voucherId && <button type="button" className="btn btn-link" onClick={() => navigate(`${AccountsConstants.VOUCHER_VIEW}?id=${row.voucherId}`)}>View invoice</button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9}>No loading advices match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={page} limit={limit} total={total} onPage={(next) => { setPage(next); load(filters, next, limit); }} onLimit={(next) => { setLimit(next); setPage(1); load(filters, 1, next); }} />
      {invoiceFor && <InvoiceDialog advice={invoiceFor} onClose={() => setInvoiceFor(null)} onSaved={(result) => { setInvoiceFor(null); setNotice(<span>Invoice {result.data.voucherNo} posted for {formatINR(result.data.totalDebit)}, due on {formatDate(result.data.dueDate)}. <button type="button" className="btn btn-link" onClick={() => navigate(`${AccountsConstants.VOUCHER_VIEW}?id=${result.data.id}`)}>View invoice</button></span>); load(); }} />}
      {cancelFor && (
        <ReasonDialog title={`Cancel ${cancelFor.adviceNo}`} label="Reason" confirm="Cancel advice" dismiss="Keep it" onClose={() => setCancelFor(null)}
          onConfirm={async (reason) => { await accountsApi.cancelAdvice(cancelFor.id, reason); setCancelFor(null); load(); }} />
      )}
    </PageShell>
  );
}

function InvoiceDialog({ advice, onClose, onSaved }) {
  const [invoiceDate, setInvoiceDate] = useState(todayInput());
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const result = await accountsApi.saveInvoice({ loadingAdviceId: advice.id, invoiceDate });
      onSaved(result);
    } catch (err) {
      setError(err);
      setSaving(false);
    }
  };
  return (
    <FormDialog
      title={`Raise invoice for ${advice.adviceNo}`}
      onClose={onClose}
      onSubmit={save}
      wide
      footer={(
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Close</button>
          <button type="submit" className="btn btn-primary" disabled={saving || !invoiceDate}>Post invoice</button>
        </>
      )}
    >
      <p className="accounts-dialog-note">{advice.customerName} · {advice.orderNo} · {advice.vehicleNo} · {formatINR(advice.totalAmount)}</p>
      <ApiAlert error={error} />
      <label className="tally-field">Invoice date *
        <input className="form-control" type="date" max={todayInput()} value={invoiceDate} onChange={(event) => setInvoiceDate(event.target.value)} />
        <span className="accounts-hint">The due date is set to this date plus the customer&apos;s credit days.</span>
      </label>
      <ItemsTable items={advice.items} />
      <EntryPreview lines={[{ ledgerName: advice.customerName, debit: advice.totalAmount, credit: 0 }, { ledgerName: "Sales", debit: 0, credit: advice.totalAmount }]} />
    </FormDialog>
  );
}

export function SalesInvoicesPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filters, setFilters] = useState({ customerId: "", fromDate: "", toDate: "" });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cancelFor, setCancelFor] = useState(null);

  const load = async (next = filters, nextPage = page, nextLimit = limit) => {
    setLoading(true);
    setError(null);
    try {
      const result = await accountsApi.invoices({ ...next, page: nextPage, limit: nextLimit });
      setRows(result?.data || []);
      setTotal(result?.pagination?.total || 0);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(filters, 1, limit);
    accountsApi.customers().then((result) => setCustomers(result?.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const setFilter = (key) => (event) => {
    const next = { ...filters, [key]: event.target.value };
    setFilters(next);
    setPage(1);
    load(next, 1, limit);
  };

  return (
    <PageShell title="Sales Invoices" actions={<button type="button" className="btn btn-outline-primary" onClick={() => navigate(AccountsConstants.LOADING_ADVICES)}>Raise from loading advice</button>}>
      <ApiAlert error={error} />
      <div className="accounts-panel accounts-filter-grid">
        <label>Customer
          <select className="form-select" value={filters.customerId} onChange={setFilter("customerId")}>
            <option value="">All</option>
            {customers.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </label>
        <label>From date<input className="form-control" type="date" value={filters.fromDate} onChange={setFilter("fromDate")} /></label>
        <label>To date<input className="form-control" type="date" value={filters.toDate} onChange={setFilter("toDate")} /></label>
      </div>
      <div className={`accounts-table-wrap ${loading ? "accounts-dim" : ""}`}>
        <table className="accounts-table">
          <thead><tr><th>Date</th><th>Invoice No</th><th>Customer</th><th>Order</th><th>Due date</th><th className="num">Amount</th><th className="num">Open amount</th><th>Receipt status</th><th>Next step</th></tr></thead>
          <tbody>
            {rows.map((row) => {
              const receiptLink = row.status === "POSTED" ? settlementLink("SALES", row) : "";
              return (
              <tr key={row.id}>
                <td>{formatDate(row.voucherDate)}</td>
                <td><button type="button" className="accounts-voucher-link" onClick={() => navigate(`${AccountsConstants.VOUCHER_VIEW}?id=${row.id}`)}>{row.voucherNo}</button></td>
                <td>{row.customerName || (row.lines && row.lines[0] && row.lines[0].ledgerName)}</td>
                <td>{row.referenceNo}</td>
                <td>{formatDate(row.dueDate)}</td>
                <td className="num">{formatINR(row.totalDebit)}</td>
                <td className="num">{formatINR(row.openAmount)}</td>
                <td><StatusChip status={row.paymentStatus || row.status} kind={row.paymentStatus ? "invoice" : undefined} /></td>
                <td className="accounts-next">
                  {receiptLink && <button type="button" className="btn btn-sm btn-primary" onClick={() => navigate(receiptLink)}>Create receipt</button>}
                  {row.status === "POSTED" && <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setCancelFor(row)}>Cancel</button>}
                </td>
              </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={9}>No sales invoices match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={page} limit={limit} total={total} onPage={(next) => { setPage(next); load(filters, next, limit); }} onLimit={(next) => { setLimit(next); setPage(1); load(filters, 1, next); }} />
      {cancelFor && (
        <ReasonDialog title={`Cancel ${cancelFor.voucherNo}`} label="Reason" confirm="Cancel voucher" dismiss="Keep voucher" onClose={() => setCancelFor(null)}
          onConfirm={async (reason) => { await accountsApi.cancelVoucher(cancelFor.id, reason); setCancelFor(null); load(); }} />
      )}
    </PageShell>
  );
}
